import type { Bot, Position, Trade, Session, MarketIndex, ExecutionTapeEntry, InfraHealth, QuoteData, MarketStatusEntry } from '@/types/bot';
import { mockMarketIndices, mockInfraHealth } from './mockData';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://hft-operator-terminal.nakar.workers.dev/api';

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.statusText}`);
  }

  return response.json();
}

// Bot API — talks to Cloudflare Worker
export const botAPI = {
  async getAll(): Promise<Bot[]> {
    const res = await fetchAPI<{ ok: boolean; bots: Bot[] }>('/bots');
    return res.bots;
  },

  async getById(id: string): Promise<Bot> {
    return fetchAPI<Bot>(`/bots/${id}`);
  },

  async start(id: string, otp?: string): Promise<void> {
    return fetchAPI<void>(`/bots/${id}/start`, {
      method: 'POST',
      body: otp ? JSON.stringify({ otp }) : undefined,
    });
  },

  async stop(id: string): Promise<void> {
    return fetchAPI<void>(`/bots/${id}/stop`, { method: 'POST' });
  },

  async setMode(id: string, mode: string): Promise<void> {
    return fetchAPI<void>(`/bots/${id}/mode`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    });
  },
};

// Position API
export const positionAPI = {
  async getByBotId(botId: string): Promise<Position[]> {
    return fetchAPI<Position[]>(`/bots/${botId}/positions`);
  },
};

// Trade API
export const tradeAPI = {
  async getByBotId(botId: string, limit = 50): Promise<Trade[]> {
    return fetchAPI<Trade[]>(`/bots/${botId}/trades?limit=${limit}`);
  },

  async getRecent(limit = 50): Promise<ExecutionTapeEntry[]> {
    return fetchAPI<ExecutionTapeEntry[]>(`/trades/recent?limit=${limit}`);
  },
};

// Session API
export const sessionAPI = {
  async getByBotId(botId: string): Promise<Session[]> {
    return fetchAPI<Session[]>(`/bots/${botId}/sessions`);
  },
};

// Market Data API — uses mock for now (real source TBD)
export const marketAPI = {
  async getIndices(): Promise<MarketIndex[]> {
    return mockMarketIndices;
  },
};

// Infra Health API — uses mock for now (real source TBD)
export const healthAPI = {
  async getInfraHealth(): Promise<InfraHealth> {
    return mockInfraHealth;
  },
};

// Audit API
export const auditAPI = {
  async getRecent(limit = 50): Promise<unknown[]> {
    const res = await fetchAPI<{ ok: boolean; audit: unknown[] }>(`/audit?limit=${limit}`);
    return res.audit || [];
  },
};

// Quotes API
export const quoteAPI = {
  async getQuotes(symbols?: string[]): Promise<Record<string, QuoteData>> {
    const params = symbols ? `?symbols=${symbols.join(',')}` : '';
    const res = await fetchAPI<{ ok: boolean; quotes: Record<string, QuoteData> }>(`/market/quotes${params}`);
    return res.quotes || {};
  },
};

// Forex API
export const forexAPI = {
  async getRates(): Promise<{ rates: Record<string, number>; base: string; timestamp: string }> {
    return fetchAPI<{ rates: Record<string, number>; base: string; timestamp: string }>('/market/forex');
  },
};

// Market Status API
export const marketStatusAPI = {
  async getStatus(): Promise<MarketStatusEntry[]> {
    const res = await fetchAPI<{ ok: boolean; markets: MarketStatusEntry[] }>('/market/status');
    return res.markets || [];
  },
};

// News API
export interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  source: string;
  url: string;
  image: string;
  publishedAt: string;
  category: string;
  sentiment: { bullishPercent?: number; bearishPercent?: number } | null;
  tickers: string[];
  provider: string;
}

export const newsAPI = {
  async getRecent(limit = 20): Promise<NewsArticle[]> {
    const res = await fetchAPI<{ ok: boolean; articles: NewsArticle[]; count: number }>(`/market/news?limit=${limit}`);
    return res.articles || [];
  },
};
